import { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { Box, Paper, SxProps, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { lang } from '../utils/lang'
import { downloadFile } from '../utils/download'
import { Button } from './atoms/Button'

interface ReserveStatement {
  id: number
  quarter: string
  invoice_number: string
  total: number
  payment_date: string
}

/**
 * The customer's quarterly reserve compensation statements (self-billed invoices) as PDF
 * downloads. Renders nothing until there is one.
 */
export function ReserveStatements(props: { sx?: SxProps<Theme> }): React.ReactElement | null {
  const { t } = useTranslation()
  const app = useApp()
  const [statements, setStatements] = useState<ReserveStatement[]>([])

  useEffect(() => {
    callAxios<{ statements: ReserveStatement[] }>({ url: '/reserve-statements' }).then(res => {
      if (res.success) setStatements(res.data.statements)
    })
  }, [])

  if (statements.length === 0) return null

  const download = (s: ReserveStatement) => {
    downloadFile(`/reserve-statements/${s.id}/pdf`, `${s.invoice_number}.pdf`).then(ok => {
      if (!ok) app.addError(t('Download failed.'))
    })
  }

  return (
    <Box sx={props.sx} data-cy="reserve-statements">
      <Typography variant="h6" sx={{ mb: 1 }}>{t('Reserve compensation statements')}</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>{t('Period')}</TableCell>
              <TableCell sx={{ color: '#fff' }} align="right">{t('Paid')}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{t('Payment date')}</TableCell>
              <TableCell/>
            </TableRow>
          </TableHead>
          <TableBody>
            {statements.map(s =>
              <TableRow key={s.id}>
                <TableCell>{s.quarter.replace(/^(\d{4})Q(\d)$/, 'Q$2/$1')}</TableCell>
                <TableCell align="right">{lang.money(s.total, 'EUR')}</TableCell>
                <TableCell>{lang.date(DateTime.fromISO(s.payment_date))}</TableCell>
                <TableCell align="right">
                  <Button size="small" data-cy={`button-Statement ${s.invoice_number}`} onClick={() => download(s)}>PDF</Button>
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
